
import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { AIStaffMember } from "@/lib/types";
import { useSingleStaff } from "./staff/useSingleStaff";
import { useAllStaff } from "./staff/useAllStaff"; 
import { useUserBusiness } from "./staff/useUserBusiness";
import { logger } from "@/lib/logger";
import { SessionContext } from "@/providers/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export function useAIStaffData() {
  const { toast } = useToast();
  const { session } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const { 
    aiStaff, 
    setAiStaff, 
    fetchAIStaff: fetchSingleStaff, 
    isLoading: isSingleStaffLoading 
  } = useSingleStaff();
  
  const { 
    allAIStaff, 
    fetchAllAIStaff: fetchAllStaff, 
    isLoading: isAllStaffLoading 
  } = useAllStaff();
  
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);
  const initialFetchAttempted = useRef(false);
  
  // Function to fetch staff with error handling
  const fetchStaffWithErrorHandling = useCallback(async (staffId?: string) => {
    if (!session) {
      logger.warn({
        message: "Attempted to fetch staff without an active session",
        context: { staffId }
      });
      return null;
    }

    try {
      const staff = await fetchSingleStaff(staffId);
      return staff;
    } catch (error) {
      logger.error({
        message: "Error in fetchStaffWithErrorHandling",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { staffId }
      });
      
      toast({
        title: "Error loading staff",
        description: "Could not load AI staff data. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [session, fetchSingleStaff, toast]);

  // Function to fetch all staff with error handling
  const fetchAllStaffWithErrorHandling = useCallback(async () => {
    // Don't fetch if no session or if we've already fetched
    if (!session) {
      logger.warn({
        message: "Attempted to fetch all staff without an active session"
      });
      return [];
    }

    try {
      const staffList = await fetchAllStaff();
      return staffList;
    } catch (error) {
      logger.error({
        message: "Error in fetchAllStaffWithErrorHandling",
        error: error instanceof Error ? error : new Error(String(error))
      });
      
      toast({
        title: "Error loading staff",
        description: "Could not load your AI staff. Please try again.",
        variant: "destructive",
      });
      return [];
    }
  }, [session, fetchAllStaff, toast]);

  // Use React Query for caching staff data with limited retries
  const { data: cachedAllStaff, isLoading: isAllStaffQueryLoading } = useQuery({
    queryKey: ['allStaff'],
    queryFn: fetchAllStaffWithErrorHandling,
    enabled: !!session,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true, // Enable refetching when window gets focus
    retry: 1,
    retryDelay: 1000,
  });

  // Set up a polling interval to refresh staff data periodically
  useEffect(() => {
    // Only start polling if user is authenticated
    if (!session) return;
    
    // Set up a 30-second polling interval for staff data
    const intervalId = setInterval(() => {
      // Only refetch if we're not already loading
      if (!isAllStaffLoading && !isSingleStaffLoading) {
        queryClient.invalidateQueries({ queryKey: ['allStaff'] });
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(intervalId);
  }, [session, isAllStaffLoading, isSingleStaffLoading, queryClient]);

  // Only fetch data once on initial component mount if session exists
  useEffect(() => {
    if (session && !hasInitiallyFetched && !initialFetchAttempted.current) {
      initialFetchAttempted.current = true;
      fetchAllStaffWithErrorHandling().then(() => {
        setHasInitiallyFetched(true);
      });
    }
  }, [session, hasInitiallyFetched, fetchAllStaffWithErrorHandling]);

  return {
    aiStaff,
    allAIStaff: cachedAllStaff || allAIStaff, // Use cached data if available
    setAiStaff,
    fetchAIStaff: fetchStaffWithErrorHandling,
    fetchAllAIStaff: fetchAllStaffWithErrorHandling,
    fetchBusinessId,
    fetchCurrentUser,
    isLoading: isSingleStaffLoading || isAllStaffQueryLoading,
    hasInitiallyFetched
  };
}
